// react
import { useState, useEffect, useCallback } from "react";
import dynamic from "next/dynamic";
import { debounce } from "lodash";
import lscache from "lscache";

// redux
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import {
  createComment,
  updateComment,
  CREATE_COMMENT,
  UPDATE_COMMENT,
} from "~/redux/actions/comment";
import { showAppModal } from "~/redux/actions/app";

// views
import { CaretDownOutlined } from "@ant-design/icons";
import { Button, Form, Dropdown, Tooltip, Input } from "antd";
import Loading from "~/components/site/Loading";
import RichContent from "~/components/app/RichContent";
import WordingTemplateNotfound from "~/components/app/WordingTemplateNotfound";
import { useWordingTemplates } from "~/hooks/useWordingTemplates";

const Editor = dynamic({
  loader: () => import("~/components/forms/Editor"),
  loading: () => <Loading />,
  ssr: false,
});

// utils
import { onInputChange } from "~/utils/form";

// 生成缓存key的函数
const generateCacheKey = (postId, parentId) => {
  return `comment_draft_${postId}_${parentId || "root"}`;
};

// 缓存操作函数 - 使用 lscache，缓存24小时
const saveCommentDraft = (postId, parentId, content, rawContent) => {
  const key = generateCacheKey(postId, parentId);
  const draft = {
    content,
    rawContent,
  };
  // 缓存24小时 (24 * 60 分钟)
  lscache.set(key, draft, 24 * 60);
};

const loadCommentDraft = (postId, parentId) => {
  const key = generateCacheKey(postId, parentId);
  return lscache.get(key);
};

const clearCommentDraft = (postId, parentId) => {
  const key = generateCacheKey(postId, parentId);
  lscache.remove(key);
};

const SUBMIT_TEXT = {
  create: "发布评论",
  update: "更新评论",
  reply: "回复评论",
};

const CommentForm = (props) => {
  let contentInput = null;
  const [form] = Form.useForm();
  const { postId, parentId, comment, title, mode = "create" } = props;
  const content = comment ? comment.content : "";
  const raw_content = comment && comment.raw_content ? comment.raw_content : "";
  const [state, setState] = useState({
    content_reset: "",
    content: content,
    content_init: raw_content,
  });

  // 添加本地状态管理搜索框值
  const [searchInputValue, setSearchInputValue] = useState("");

  const {
    isAdmin,
    wordingTemplates,
    loading,
    setKeyword,
    currentTemplate,
    setCurrentTemplate,
    refetchWordingTemplates,
  } = useWordingTemplates({ scenario: "quick_comment" });

  // props
  const disabled =
    props.request[CREATE_COMMENT] || props.request[UPDATE_COMMENT];
  const submitText = SUBMIT_TEXT[mode];

  const handleTemplateSearchDebounced = useCallback(
    debounce((value) => {
      setKeyword(value);
    }, 300),
    [setKeyword]
  );

  // 处理搜索框值变化
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchInputValue(value); // 更新本地状态
    handleTemplateSearchDebounced(value); // 延迟更新 keyword
  };

  // 处理搜索按钮点击
  const handleSearch = (value) => {
    setSearchInputValue(value);
    setKeyword(value);
  };

  const handleTemplateSelect = (item) => {
    const template = wordingTemplates.find(
      (template) => template.id === item.key
    );
    setCurrentTemplate(template);
    form.setFieldValue("content_raw", template.content_raw);
    setState({
      ...state,
      content: template.content,
      raw_content: template.content_raw,
    });
  };

  // handles
  const handleInputChange = onInputChange({ state, setState });
  const requireSignin = () => {
    if (!props.token) {
      // 保存当前输入的内容到缓存
      if (state.content || state.raw_content) {
        saveCommentDraft(postId, parentId, state.content, state.raw_content);
      }
      props.showAppModal("PhoneSigninModal");
      contentInput && contentInput.blur();
      return true;
    } else {
      return false;
    }
  };
  const handleSubmit = () => {
    if (requireSignin()) {
      return;
    }
    const commentParams = {
      postId: postId,
      parentId: parentId,
      content: state.content,
      raw_content: state.raw_content,
      template_id: currentTemplate?.id,
    };
    if (mode === "update") {
      props.updateComment(
        props.token,
        {
          ...commentParams,
          id: comment.id,
        },
        () => {
          handleCancel();
        }
      );
    } else {
      // props.createComment(
      //   props.token,
      //   {
      //     ...commentParams,
      //   },
      //   (comment) => {
      //     handleCancel();
      //     setTimeout(() => {
      //       const el = document.querySelector(`#comment-${comment.id}`);
      //       if (el && el.scrollIntoView) {
      //         el.scrollIntoView({ behavior: "smooth" });
      //       }
      //     }, 500);
      //   }
      // );

      const myEle = props.user.ele;
      if (myEle >= 0) {
        props.createComment(
          props.token,
          {
            ...commentParams,
          },
          (comment) => {
            // 清除缓存的草稿
            clearCommentDraft(postId, parentId);
            handleCancel();
            setTimeout(() => {
              const el = document.querySelector(`#comment-${comment.id}`);
              if (el && el.scrollIntoView) {
                el.scrollIntoView({ behavior: "smooth" });
              }
            }, 500);
          }
        );
      } else {
        props.showAppModal("RestrictedOperationModal", {
          operation: "评论",
        });
      }
    }
  };

  const handleContentChange = async (value, event) => {
    setState((prevState) => ({
      ...prevState,
      content: value.html,
      raw_content: value.text,
    }));
    form.setFieldValue("content_raw", value.text);
  };

  const handleCancel = () => {
    // 清除缓存的草稿
    clearCommentDraft(postId, parentId);
    setState({
      // ...state,
      content: "",
      content_init: "",
      raw_content: "",
      currentTemplate: null,
      content_reset: new Date(),
    });
    form.setFieldsValue({
      content_raw: "",
    });
    setCurrentTemplate(null);

    props.onCancel && props.onCancel();
  };

  useEffect(() => {
    if (mode !== "create") {
      contentInput && contentInput.focus();
    }
  }, []);

  // 加载缓存的评论草稿
  useEffect(() => {
    // 只在创建模式且用户已登录时加载缓存
    if (mode === "create" && props.token && !comment) {
      const cachedDraft = loadCommentDraft(postId, parentId);
      if (cachedDraft && (cachedDraft.content || cachedDraft.rawContent)) {
        setState((prevState) => ({
          ...prevState,
          content: cachedDraft.content || "",
          raw_content: cachedDraft.rawContent || "",
          content_init: cachedDraft.rawContent || "",
        }));
        form.setFieldValue("content_raw", cachedDraft.rawContent || "");
      }
    }
  }, [postId, parentId, mode, props.token, comment]);

  const cancelButton = (
    <Button
      disabled={disabled}
      onClick={handleCancel}
    >
      取消
    </Button>
  );

  return (
    <Form
      onFinish={handleSubmit}
      form={form}
    >
      <Form.Item
        style={{ marginBottom: 15 }}
        name="content_raw"
      >
        <Editor
          name="content_raw"
          defaultValue={state.content_init}
          value={state.content_raw}
          reset={state.content_reset}
          token={props.token}
          title={title}
          onChange={handleContentChange}
          placeholder={`
[注 意]
    有价值的评论会得到大家的点赞支持；
    请勿通过评论暴露联系方式，不符则删；
    评论被删后，会扣除相应的电量和经验。
          `}
          height={190}
        />
      </Form.Item>

      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <div>
          <Button
            type="primary"
            htmlType="submit"
            className="mb-10 mr-10"
            disabled={disabled}
          >
            {submitText}
          </Button>
          {mode !== "create" && cancelButton}
        </div>
        {isAdmin && (
          <Dropdown
            trigger={["click"]}
            placement="bottomRight"
            onOpenChange={(open) => {
              if (open) {
                setKeyword("");
                setSearchInputValue(""); // 清空本地搜索框状态
                refetchWordingTemplates();
              }
            }}
            menu={{
              items: wordingTemplates.map((template, index) => ({
                key: template.id,
                label: (
                  <div>
                    <Tooltip
                      placement="right"
                      title={<RichContent html={template.content} />}
                      overlayClassName="wording-template-tooltip"
                    >
                      <div
                        style={{
                          borderTop: index > 0 ? "1px solid #e8e8e8" : "none",
                          paddingTop: index > 0 ? "10px" : "0",
                          width: "100%",
                        }}
                      >
                        #{index + 1} {template.title}
                      </div>
                    </Tooltip>
                  </div>
                ),
              })),
              selectable: false,
              onClick: handleTemplateSelect,
            }}
            dropdownRender={(menu) => {
              return (
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <Input.Search
                    placeholder="搜索模板"
                    allowClear
                    enterButton
                    value={searchInputValue} // 使用本地状态
                    onChange={handleSearchChange}
                    onSearch={handleSearch}
                    loading={loading}
                  />
                  {wordingTemplates.length > 0 && menu}
                  {wordingTemplates.length === 0 && !loading && (
                    <WordingTemplateNotfound />
                  )}
                </div>
              );
            }}
          >
            <Button type="primary">
              <span>使用模板评论</span>
              <CaretDownOutlined />
            </Button>
          </Dropdown>
        )}
      </div>
    </Form>
  );
};

const mapStateToProps = (state) => ({
  token: state.user.token,
  user: state.user.user,
  request: state.app.request,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      createComment,
      updateComment,
      showAppModal,
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(CommentForm);
